import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { baseURL } from "../../config";
import { openNotification } from "../../components/common/helper";

const initialState = {
  isLoading: false,
  isSnapshotLoading: false,
  error: null,
  claims: [],
  property: {},
  claimDetails: { isLoading: false, claim: null },
  appealClaims: [],
  appealProperty: {},
  appealLoading: false,
  snapshot: {},
  claim_type: localStorage.getItem("claimType") || "Hospital",
  status_filter: localStorage.getItem("statusFilter") || "Pending",
  visit_filter: localStorage.getItem("visitFilter") || "All",
};

export const listClaims = createAsyncThunk(
  "claims/List",
  async (
    {
      size,
      page,
      source,
      visitType,
      status,
      sort,
      q,
      member_number,
      member_name,
      claim_id,
      provider_name,
      fromDate,
      toDate,
    },
    thunkAPI,
  ) => {
    try {
      // Ensure we have a valid sort parameter (default to 'Desc' if not provided or invalid)
      const validSort =
        sort && (sort === "Asc" || sort === "Desc") ? sort : "Desc";

      // Build query parameters for the API
      const queryParams = new URLSearchParams();

      // Pagination parameters
      queryParams.append("pageSize", size);
      queryParams.append("page", page);

      // Source/type parameter
      if (source && source !== "All") {
        queryParams.append("type", source);
      }

      // Status filter
      if (status && status !== "All") {
        queryParams.append("status", status);
      }

      // Visit type filter (only for non-Mental Health claims)
      if (visitType && visitType !== "All" && source !== "Mental_Health") {
        queryParams.append("visitType", visitType);
      }

      // Sort parameter - always include a valid sort value
      queryParams.append("sortOrder", validSort);

      // Search query
      if (q && q.trim()) {
        queryParams.append("q", q.trim());
      }

      // Member number
      if (member_number && member_number.trim()) {
        queryParams.append("member_number", member_number.trim());
      }

      // Member name
      if (member_name && member_name.trim()) {
        queryParams.append("member_name", member_name.trim());
      }

      // Claim ID
      if (claim_id && claim_id.trim()) {
        queryParams.append("claim_id", claim_id.trim());
      }

      // Provider name
      if (provider_name && provider_name.trim()) {
        queryParams.append("provider_name", provider_name.trim());
      }

      // Date range (API expects startDate/endDate)
      if (fromDate) {
        queryParams.append("startDate", fromDate);
      }

      if (toDate) {
        queryParams.append("endDate", toDate);
      }

      let url;

      // Handle Reimbursement Claims differently
      if (source === "Reimbursement_Claims") {
        const reimbursedParams = new URLSearchParams();
        reimbursedParams.append("size", size);
        reimbursedParams.append("page", page);

        if (status && status !== "All") {
          reimbursedParams.append("status", status);
        }

        // Always include a valid sort value for reimbursement claims
        reimbursedParams.append("sort", `createdTimestamp,${validSort}`);

        // Add search parameters for reimbursement claims
        if (q && q.trim()) reimbursedParams.append("q", q.trim());
        if (member_number && member_number.trim())
          reimbursedParams.append("member_number", member_number.trim());
        if (member_name && member_name.trim())
          reimbursedParams.append("member_name", member_name.trim());
        if (claim_id && claim_id.trim())
          reimbursedParams.append("claim_id", claim_id.trim());
        if (fromDate) reimbursedParams.append("fromDate", fromDate);
        if (toDate) reimbursedParams.append("toDate", toDate);

        url = `${baseURL}reimbursed-claim?${reimbursedParams.toString()}`;
      } else {
        queryParams.append("sort", `createdTimestamp,${validSort}`);
        // Regular claims API
        url = `${baseURL}claims?${queryParams.toString()}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || "Failed to fetch claims");
      }

      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const searchClaims = createAsyncThunk(
  "claims/Search",
  async ({ query, size, page }, thunkAPI) => {
    return await fetch(
      `${baseURL}pas/claim/search?query=${query}&size=${size}&page=${page}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      },
    )
      .then(async (res) => {
        return res.json().then((resJson) => {
          if (!res.ok) throw new Error(resJson?.error);
          return resJson;
        });
      })
      .catch((e) => thunkAPI.rejectWithValue(e.message));
  },
);

export const listPharmacyClaims = createAsyncThunk(
  "claims/ListPharmacy",
  async ({ size, page }, thunkAPI) => {
    return await fetch(
      `${baseURL}pas/claim/pharmacy/list?size=${size}&page=${page}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      },
    )
      .then(async (res) => {
        return res.json().then((resJson) => {
          if (!res.ok) throw new Error(resJson?.error);
          return resJson;
        });
      })
      .catch((e) => thunkAPI.rejectWithValue(e.message));
  },
);

export const getClaimDetails = createAsyncThunk(
  "claims/getClaim",
  async ({ claim_id, source }, thunkAPI) => {
    return await fetch(
      source === "Reimbursement_Claims"
        ? `${baseURL}reimbursed-claim/${claim_id}/details?benefits=true`
        : `${baseURL}claims/${claim_id}/details`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      },
    )
      .then(async (res) => {
        return res.json().then((resJson) => {
          if (!res.ok) throw new Error(resJson?.error);
          return resJson;
        });
      })
      .catch((e) => thunkAPI.rejectWithValue(e.message));
  },
);

export const getClaimSnapshot = createAsyncThunk(
  "claims/getSnapshot",
  async (args, thunkAPI) => {
    return await fetch(`${baseURL}claims/stats`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
    })
      .then(async (res) => {
        return res.json().then((resJson) => {
          if (!res.ok) throw new Error(resJson?.error);
          return resJson;
        });
      })
      .catch((e) => thunkAPI.rejectWithValue(e.message));
  },
);

export const listClaimAppeals = createAsyncThunk(
  "claims/listClaimAppeals",
  async ({ page = 0, size = 10, sort = "Desc" }, thunkAPI) => {
    try {
      const response = await fetch(
        `${baseURL}claim-appeal/retrieve-list?page=${page}&size=${size}&sort=${sort}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || "Failed to fetch appeal claims");
      }
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const updateAppealStatus = createAsyncThunk(
  "claims/updateAppealStatus",
  async ({ claim_id, status, approved_amount, remarks }, thunkAPI) => {
    try {
      const response = await fetch(
        `${baseURL}claim-appeal/${claim_id}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          body: JSON.stringify({
            status,
            approved_amount: approved_amount ? Number(approved_amount) : undefined,
            remarks,
          }),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || "Failed to update appeal status");
      }
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const ClaimSlice = createSlice({
  name: "claims",
  initialState: initialState,
  reducers: {
    logOut: (state, actions) => {
      state.isLoading = false;
      state.isAuthenticated = false;
      state.token = null;
      localStorage.clear();
    },
    clearErr: (state, actions) => {
      state.error = null;
    },
    setClaimType: (state, action) => {
      state.claim_type = action.payload;
      localStorage.setItem("claimType", action.payload);
    },
    setStatusFilter: (state, action) => {
      console.log("in here");
      state.status_filter = action.payload;
      localStorage.setItem("statusFilter", action.payload);
    },
    setVisitFilter: (state, action) => {
      state.visit_filter = action.payload;
      localStorage.setItem("visitFilter", action.payload);
    },
  },
  extraReducers(builder) {
    builder.addCase(listClaims.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(listClaims.fulfilled, (state, action) => {
      state.claims = action.payload.result?.content;
      state.property.total_elements =
        action.payload.result?.total_elements ||
        action.payload.result?.property?.total_elements;
      state.isLoading = false;
    });
    builder.addCase(listClaims.rejected, (state, action) => {
      openNotification(action.payload, "Error");
      state.isLoading = false;
    });
    builder.addCase(searchClaims.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(searchClaims.fulfilled, (state, action) => {
      state.claims = action.payload.result.content;
      state.property.total_elements =
        action.payload.result?.total_elements ||
        action.payload.result?.property?.total_elements;
      state.isLoading = false;
    });
    builder.addCase(searchClaims.rejected, (state, action) => {
      openNotification(action.payload, "Error");
      state.isLoading = false;
    });
    builder.addCase(listPharmacyClaims.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(listPharmacyClaims.fulfilled, (state, action) => {
      state.claims = action.payload.result.content;
      state.property.total_elements = action.payload.result?.total_elements;
      state.isLoading = false;
    });
    builder.addCase(listPharmacyClaims.rejected, (state, action) => {
      openNotification(action.payload, "Error");
      state.isLoading = false;
    });
    builder.addCase(getClaimDetails.pending, (state) => {
      state.claimDetails.isLoading = true;
    });
    builder.addCase(getClaimDetails.fulfilled, (state, action) => {
      state.claimDetails.claim = action.payload.result;
      state.claimDetails.isLoading = false;
    });
    builder.addCase(getClaimDetails.rejected, (state, action) => {
      openNotification(action.payload, "Error");
      state.claimDetails.isLoading = false;
    });
    builder.addCase(getClaimSnapshot.pending, (state) => {
      state.isSnapshotLoading = true;
    });
    builder.addCase(getClaimSnapshot.fulfilled, (state, action) => {
      // state.claims = action.payload.result.content
      state.snapshot = action.payload.result;
      state.isSnapshotLoading = false;
    });
    builder.addCase(getClaimSnapshot.rejected, (state, action) => {
      openNotification(action.payload, "Error");
      state.isSnapshotLoading = false;
    });
    builder.addCase(listClaimAppeals.pending, (state) => {
      state.appealLoading = true;
    });
    builder.addCase(listClaimAppeals.fulfilled, (state, action) => {
      const payload = action.payload?.result || action.payload;
      state.appealClaims = payload?.content || [];
      state.appealProperty = payload?.property || {};
      state.appealLoading = false;
    });
    builder.addCase(listClaimAppeals.rejected, (state, action) => {
      openNotification(action.payload, "Error");
      state.appealLoading = false;
    });
    builder.addCase(updateAppealStatus.pending, (state) => {
      state.claimDetails.isLoading = true;
    });
    builder.addCase(updateAppealStatus.fulfilled, (state, action) => {
      openNotification("Appeal status updated successfully", "Success");
      state.claimDetails.isLoading = false;
    });
    builder.addCase(updateAppealStatus.rejected, (state, action) => {
      openNotification(action.payload, "Error");
      state.claimDetails.isLoading = false;
    });
  },
});
export const { clearErr, setClaimType, setStatusFilter, setVisitFilter } =
  ClaimSlice.actions;
export default ClaimSlice.reducer;
